import React, { useCallback, useEffect, useMemo, useState } from "react";
import { RefreshCw, Search, FileText, ClipboardCopy, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";

/**
 * AdminDraftsPage — the recovery tool for "lost" intake submissions.
 *
 * When a client fills out the multi-step intake form but never clicks Submit
 * (or the submit fails), their answers are still auto-saved to the
 * `intake_drafts` collection. This page surfaces every such draft so Riley
 * can find and follow up with the client.
 *
 * Also includes a cross-collection search that hits the new
 * `/api/admin/intakes/search` endpoint, which spans:
 *   \u2022 fully-submitted intakes
 *   \u2022 abandoned drafts
 *   \u2022 raw payload audit-log (for rejected 422/400 submissions)
 */
export default function AdminDraftsPage() {
    const { authedFetch } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState("");
    const [onlyWithContent, setOnlyWithContent] = useState(true);
    const [openDraft, setOpenDraft] = useState(null); // full draft doc
    const [openLoading, setOpenLoading] = useState(false);

    // Global search across intakes + drafts + raw audit
    const [searchQ, setSearchQ] = useState("");
    const [searchResults, setSearchResults] = useState(null);
    const [searching, setSearching] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await authedFetch("/admin/intakes/drafts?limit=500");
            setItems(data.items || []);
        } catch (e) {
            setError(e?.response?.data?.detail || "Failed to load drafts.");
        } finally {
            setLoading(false);
        }
    }, [authedFetch]);

    useEffect(() => {
        load();
    }, [load]);

    const filtered = useMemo(() => {
        const q = filter.trim().toLowerCase();
        return items.filter((d) => {
            if (onlyWithContent && !d.has_content) return false;
            if (!q) return true;
            return (
                (d.first_name || "").toLowerCase().includes(q) ||
                (d.last_name || "").toLowerCase().includes(q) ||
                (d.email || "").toLowerCase().includes(q) ||
                (d.phone || "").toLowerCase().includes(q) ||
                (d.primary_concern_preview || "").toLowerCase().includes(q)
            );
        });
    }, [items, filter, onlyWithContent]);

    const openDraftDetail = async (draftId) => {
        setOpenLoading(true);
        try {
            const { data } = await authedFetch(`/admin/intakes/drafts/${draftId}`);
            setOpenDraft(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load draft.");
        } finally {
            setOpenLoading(false);
        }
    };

    const runSearch = useCallback(async () => {
        const q = searchQ.trim();
        if (!q) {
            setSearchResults(null);
            return;
        }
        setSearching(true);
        try {
            const { data } = await authedFetch(
                `/admin/intakes/search?q=${encodeURIComponent(q)}`,
            );
            setSearchResults(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Search failed.");
        } finally {
            setSearching(false);
        }
    }, [searchQ, authedFetch]);

    const copyDraftJson = async () => {
        if (!openDraft) return;
        try {
            await navigator.clipboard.writeText(
                JSON.stringify(openDraft, null, 2),
            );
            toast.success("Draft JSON copied to clipboard.");
        } catch {
            toast.error("Copy failed — select the text below manually.");
        }
    };

    return (
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
                <div>
                    <h1 className="font-display text-3xl text-moss">
                        Abandoned drafts & recovery
                    </h1>
                    <p className="mt-1 text-sm text-foreground/65 max-w-2xl">
                        Every keystroke on the intake form auto-saves to a draft.
                        Use this page to find clients who started but never
                        submitted — or to search across every submission,
                        draft, and raw audit-log record.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={load}
                    data-testid="drafts-refresh"
                    className="inline-flex items-center gap-2 rounded-xl bg-sand px-4 py-2 text-sm text-moss hover:bg-sand/80"
                >
                    <RefreshCw className="h-4 w-4" />
                    Refresh
                </button>
            </div>

            {/* --- Global search (all collections) --- */}
            <div className="rounded-2xl border border-border/80 bg-white/85 p-4 mb-6">
                <div className="flex items-center gap-2 text-sm font-medium text-moss mb-2">
                    <Search className="h-4 w-4" /> Search everything (submitted intakes + drafts + raw audit log)
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                    <input
                        type="search"
                        placeholder="e.g. Eunice, or an email fragment..."
                        value={searchQ}
                        onChange={(e) => setSearchQ(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && runSearch()}
                        data-testid="drafts-global-search-input"
                        className="flex-1 rounded-xl bg-cream border border-border/80 px-3 py-2.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage"
                    />
                    <button
                        type="button"
                        onClick={runSearch}
                        disabled={searching}
                        data-testid="drafts-global-search-btn"
                        className="rounded-xl bg-moss text-cream px-4 py-2.5 text-sm font-medium hover:bg-moss/90 disabled:opacity-60"
                    >
                        {searching ? "Searching..." : "Search all"}
                    </button>
                </div>
                {searchResults && (
                    <div
                        data-testid="drafts-search-results"
                        className="mt-4 space-y-4 text-sm"
                    >
                        <p className="text-foreground/70">
                            Found <b>{searchResults.total}</b> match(es) for
                            <b className="text-terracotta"> &ldquo;{searchResults.query}&rdquo;</b>.
                        </p>
                        <SearchGroup
                            title="Submitted intakes"
                            rows={searchResults.intakes}
                            renderRow={(r) => (
                                <div key={r.id}>
                                    <p className="font-medium">
                                        {r.first_name} {r.last_name}{" "}
                                        <span className="text-foreground/50 text-xs">
                                            · {r.email}
                                        </span>
                                    </p>
                                    <p className="text-xs text-foreground/60">
                                        Submitted {new Date(r.submitted_at).toLocaleString()}{" "}
                                        · status:{" "}
                                        <span className="text-moss">{r.status}</span>
                                    </p>
                                    <a
                                        href={`/admin/intakes/${r.id}`}
                                        className="text-terracotta text-xs hover:underline"
                                    >
                                        Open intake →
                                    </a>
                                </div>
                            )}
                        />
                        <SearchGroup
                            title="Drafts (not yet submitted)"
                            rows={searchResults.drafts}
                            renderRow={(r) => (
                                <div key={r.draft_id}>
                                    <p className="font-medium">
                                        {r.first_name || (
                                            <span className="text-foreground/40">
                                                (no name)
                                            </span>
                                        )}{" "}
                                        {r.last_name}{" "}
                                        <span className="text-foreground/50 text-xs">
                                            · {r.email || "no email"}
                                        </span>
                                    </p>
                                    <p className="text-xs text-foreground/60">
                                        Last edited {new Date(r.updated_at).toLocaleString()}
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => openDraftDetail(r.draft_id)}
                                        className="text-terracotta text-xs hover:underline"
                                    >
                                        View full draft →
                                    </button>
                                </div>
                            )}
                        />
                        <SearchGroup
                            title="Raw audit log (rejected / malformed submissions)"
                            rows={searchResults.raw_audit}
                            renderRow={(r) => (
                                <div key={r.audit_id}>
                                    <p className="font-medium flex items-center gap-1.5">
                                        {r.first_name || "(no name)"} {r.last_name}
                                        {r.validation_error && (
                                            <span className="inline-flex items-center gap-0.5 text-xs text-terracotta">
                                                <AlertCircle className="h-3 w-3" />
                                                validation rejected
                                            </span>
                                        )}
                                    </p>
                                    <p className="text-xs text-foreground/60">
                                        Received {new Date(r.received_at).toLocaleString()}{" "}
                                        {r.ip && `· from ${r.ip}`}
                                        {r.intake_id && (
                                            <>
                                                {" "}
                                                · linked to intake{" "}
                                                <a
                                                    href={`/admin/intakes/${r.intake_id}`}
                                                    className="text-terracotta hover:underline"
                                                >
                                                    {r.intake_id.slice(0, 8)}…
                                                </a>
                                            </>
                                        )}
                                    </p>
                                    <p className="text-xs text-foreground/50 font-mono">
                                        ref: {r.audit_id}
                                    </p>
                                </div>
                            )}
                        />
                    </div>
                )}
            </div>

            {/* --- Filter within drafts --- */}
            <div className="rounded-2xl border border-border/80 bg-white/85 p-4 flex flex-col sm:flex-row gap-3 mb-6">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/50" />
                    <input
                        type="search"
                        placeholder="Filter drafts by name, email, or concern..."
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        data-testid="drafts-filter-input"
                        className="w-full rounded-xl bg-cream border border-border/80 pl-9 pr-3 py-2.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage"
                    />
                </div>
                <label className="inline-flex items-center gap-2 text-sm text-foreground/75">
                    <input
                        type="checkbox"
                        checked={onlyWithContent}
                        onChange={(e) => setOnlyWithContent(e.target.checked)}
                        className="h-4 w-4 rounded border-border/80 text-moss focus:ring-sage"
                        data-testid="drafts-only-with-content"
                    />
                    Only drafts with real content
                </label>
            </div>

            {/* --- Draft table --- */}
            <div className="rounded-2xl border border-border/80 bg-white/90 overflow-hidden">
                {loading ? (
                    <div className="p-10 text-center text-foreground/55">
                        Loading drafts...
                    </div>
                ) : error ? (
                    <div className="p-10 text-center text-terracotta">{error}</div>
                ) : filtered.length === 0 ? (
                    <div className="p-10 text-center text-foreground/55">
                        No drafts match your filter.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table
                            data-testid="drafts-table"
                            className="w-full text-sm"
                        >
                            <thead className="bg-sand/50 text-left text-xs uppercase tracking-wider text-foreground/65">
                                <tr>
                                    <th className="p-4">Client</th>
                                    <th className="p-4">Concern preview</th>
                                    <th className="p-4">Last edited</th>
                                    <th className="p-4"></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border/70">
                                {filtered.map((d) => (
                                    <tr
                                        key={d.draft_id}
                                        data-testid="drafts-row"
                                        className="hover:bg-sand/30 transition-colors"
                                    >
                                        <td className="p-4">
                                            <p className="font-medium text-foreground/90">
                                                {d.first_name || (
                                                    <span className="text-foreground/40">
                                                        (no name yet)
                                                    </span>
                                                )}{" "}
                                                {d.last_name}
                                            </p>
                                            <p className="text-xs text-foreground/60">
                                                {d.email || (
                                                    <span className="text-foreground/40">
                                                        no email
                                                    </span>
                                                )}
                                                {d.phone && (
                                                    <>
                                                        {" · "}
                                                        {d.phone}
                                                    </>
                                                )}
                                            </p>
                                        </td>
                                        <td className="p-4 max-w-md">
                                            <p className="text-foreground/80 line-clamp-2">
                                                {d.primary_concern_preview || (
                                                    <span className="text-foreground/40">
                                                        —
                                                    </span>
                                                )}
                                            </p>
                                        </td>
                                        <td className="p-4 text-foreground/65 text-xs">
                                            {new Date(d.updated_at).toLocaleDateString()}
                                            <br />
                                            <span className="text-foreground/45">
                                                {new Date(d.updated_at).toLocaleTimeString()}
                                            </span>
                                        </td>
                                        <td className="p-4 text-right">
                                            <button
                                                type="button"
                                                data-testid="drafts-view-btn"
                                                onClick={() =>
                                                    openDraftDetail(d.draft_id)
                                                }
                                                className="inline-flex items-center gap-1 text-sm font-medium text-moss hover:text-terracotta"
                                            >
                                                <FileText className="h-4 w-4" />
                                                View full draft
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* --- Draft detail modal --- */}
            <Dialog
                open={!!openDraft || openLoading}
                onOpenChange={(open) => {
                    if (!open) setOpenDraft(null);
                }}
            >
                <DialogContent
                    data-testid="draft-detail-dialog"
                    className="max-w-3xl max-h-[85vh] overflow-y-auto"
                >
                    <DialogHeader>
                        <DialogTitle className="text-moss">
                            {openLoading
                                ? "Loading draft…"
                                : `Draft: ${openDraft?.data?.first_name || "(no name)"} ${openDraft?.data?.last_name || ""}`}
                        </DialogTitle>
                        <DialogDescription>
                            Full auto-saved answers as of{" "}
                            {openDraft?.updated_at
                                ? new Date(openDraft.updated_at).toLocaleString()
                                : "—"}
                            .
                        </DialogDescription>
                    </DialogHeader>
                    {openDraft && (
                        <>
                            <div className="flex justify-end mb-2">
                                <button
                                    type="button"
                                    onClick={copyDraftJson}
                                    data-testid="draft-copy-json"
                                    className="inline-flex items-center gap-1.5 rounded-xl bg-sand px-3 py-1.5 text-xs text-moss hover:bg-sand/80"
                                >
                                    <ClipboardCopy className="h-3.5 w-3.5" />
                                    Copy JSON
                                </button>
                            </div>
                            <pre className="rounded-xl bg-cream/60 border border-border/70 p-4 text-xs text-foreground/85 overflow-x-auto whitespace-pre-wrap">
                                {JSON.stringify(openDraft.data, null, 2)}
                            </pre>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

const SearchGroup = ({ title, rows, renderRow }) => {
    if (!rows || rows.length === 0) return null;
    return (
        <div className="rounded-xl border border-border/70 bg-sand/30 p-3">
            <p className="font-accent text-xs text-terracotta mb-2">
                {title} ({rows.length})
            </p>
            <div className="space-y-3">
                {rows.map((r, i) =>
                    React.cloneElement(renderRow(r), { key: r.id || r.draft_id || r.audit_id || i }),
                )}
            </div>
        </div>
    );
};
